/**
 * 4. geçiş — GI geçmişi (tam çözünürlük, iki doku).
 *
 * Yalnız GI ve AO biriktirilir, ekranın son rengi değil; doku deseni bu yüzden bulanmaz.
 *
 * Bir pikselin geçen karedeki yeri kameradan hesaplanır (derinlik → dünya → önceki kamera).
 * O yerde kayıtlı derinlik bu noktanınkiyle tutmuyorsa (yeni açılan yer, ekran dışı)
 * geçmiş silinir; piksel yeniden başlar ve yumuşatma geçişi onu bir süre güçlü yumuşatır.
 *
 * Renk dokusu: vec4( gi.rgb, ao )
 * Bilgi dokusu: vec4( geçmiş uzunluğu n, viewZ, doldurmada veri yok mu (1/0), bu kare taranan nokta mı (1/0) )
 */

import { Fn, If, abs, float, floor, max, min, mix, uv, vec2, vec4, getScreenPosition } from 'three/tsl';
import { readSurface } from './screenSurface.js';

// Geçen karedeki derinlik, beklenenden %3'ten fazla farklıysa başka yüzeydir.
const HISTORY_DEPTH_TOLERANCE = 0.03;
// Kamera hareketinde geçmiş kısaltma (readHistory).
const MOTION_RESPONSE = 0.25;
const MIN_HISTORY_IN_MOTION = 4;

/** Bu karenin değeri: doldurma; orada veri yoksa ve kenar taraması açıksa onun sonucu. */
function readCurrentFrame( node, screenUv ) {

	const filled = node._fillTexture.sample( screenUv );
	const useRefine = node._refineEnabled.greaterThan( 0.5 ).and( filled.a.lessThan( 0 ) );

	return useRefine.select( node._refineTexture.sample( screenUv ), filled ).toVar();

}

/** Bu piksel bu kare taranan nokta mı? (tracePass ile aynı koordinat; yalnız hata ayıklama için) */
function isCellSamplePoint( node, screenUv ) {

	const pixel = floor( screenUv.mul( node._fullResolution ) );
	const pixelInCell = pixel.sub( floor( pixel.div( node._stride ) ).mul( node._stride ) );

	return pixelInCell.x.equal( node._sampleOffset.x ).and( pixelInCell.y.equal( node._sampleOffset.y ) );

}

/**
 * Aynı yüzey noktasının geçen karedeki geçmişi.
 *
 * Ekran-uzayı GI bakış açısına bağlıdır: kamera dönünce bir noktanın GI'sı da değişir.
 * Bu yüzden ekranda hızlı kayan pikselde geçmiş kısaltılır (aksi halde eski GI iz bırakır).
 * Kare başına 4 piksel kayma → en fazla yarısı, 12 piksel → dörtte biri, en az 4 kare.
 */
function readHistory( node, screenUv, center ) {

	const worldPosition = node._cameraWorldMatrix.mul( vec4( center.position, 1 ) );
	const previousViewPosition = node._previousViewMatrix.mul( worldPosition ).xyz.toVar();
	const previousUv = getScreenPosition( previousViewPosition, node._previousProjectionMatrix ).toVar();

	const info = node._historyInfoRead.sample( previousUv );
	const insideScreen = previousUv.greaterThanEqual( vec2( 0 ) ).all().and( previousUv.lessThanEqual( vec2( 1 ) ).all() );
	const sameSurface = abs( info.y.sub( previousViewPosition.z ) ).lessThan( abs( previousViewPosition.z ).mul( HISTORY_DEPTH_TOLERANCE ) );
	const valid = insideScreen.and( sameSurface ).and( info.x.greaterThan( 0 ) );

	const motionPixels = previousUv.sub( screenUv ).mul( node._fullResolution ).length();
	const motionCap = min( max( node.historyLength.div( motionPixels.mul( MOTION_RESPONSE ).add( 1 ) ), MIN_HISTORY_IN_MOTION ), node.historyLength );

	return {
		valid,
		length: valid.select( min( info.x, motionCap ), 0 ).toVar(),
		color: node._historyColorRead.sample( previousUv ),
	};

}

export function buildHistoryColorPass( node ) {

	return Fn( () => {

		const screenUv = uv();
		const center = readSurface( node, screenUv );
		const result = vec4( 0, 0, 0, 1 ).toVar();

		If( center.isSky.not(), () => {

			const current = readCurrentFrame( node, screenUv );
			const history = readHistory( node, screenUv, center );

			If( current.a.greaterThanEqual( 0 ), () => {

				// n kare birikmişse yeni karenin payı 1/(n+1). Geçmiş uzunluğu en küçük payı belirler.
				const currentShare = float( 1 ).div( min( history.length, node.historyLength.sub( 1 ) ).add( 1 ) );
				result.assign( history.valid.select( mix( history.color, current, currentShare ), current ) );

			} ).ElseIf( history.valid, () => {

				// Bu kare veri yok (kenar taraması kapalı): geçmiş olduğu gibi kalır.
				result.assign( history.color );

			} );

		} );

		return result;

	} )();

}

export function buildHistoryInfoPass( node ) {

	return Fn( () => {

		const screenUv = uv();
		const center = readSurface( node, screenUv );
		const result = vec4( 0 ).toVar(); // gökyüzü: geçmiş yok

		If( center.isSky.not(), () => {

			const filledMissing = node._fillTexture.sample( screenUv ).a.lessThan( 0 );
			const hasCurrent = readCurrentFrame( node, screenUv ).a.greaterThanEqual( 0 );
			const history = readHistory( node, screenUv, center );

			const length = hasCurrent.select( min( history.length.add( 1 ), node.historyLength ), history.length );

			result.assign( vec4( length, center.position.z, filledMissing.select( 1, 0 ), isCellSamplePoint( node, screenUv ).select( 1, 0 ) ) );

		} );

		return result;

	} )();

}
